import { readJson, writeJson } from './storage';
import { createStore, useStore } from './store';

export type Theme = 'system' | 'light' | 'dark';

export interface UserSettings {
  displayName: string;
  theme: Theme;
  audioInputId: string;
  audioOutputId: string;
  videoInputId: string;
  mirrorVideo: boolean;
  hdVideo: boolean;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  joinMuted: boolean;
  joinVideoOff: boolean;
  showPreview: boolean;
  confirmLeave: boolean;
  showTimer: boolean;
  showNames: boolean;
  hideSelfView: boolean;
  chatSound: boolean;
  notifyJoinLeave: boolean;
  shareBorder: boolean;
  /** Last choices in the share dialog. */
  shareSurface: 'monitor' | 'window' | 'browser';
  shareAudio: boolean;
  shareOptimizeVideo: boolean;
  /** Open the always-on-top floating window (controls + cameras) when sharing. */
  shareFloating: boolean;
}

export const DEFAULT_SETTINGS: UserSettings = {
  displayName: '',
  theme: 'system',
  audioInputId: 'default',
  audioOutputId: 'default',
  videoInputId: '',
  mirrorVideo: true,
  hdVideo: false,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  joinMuted: false,
  joinVideoOff: false,
  showPreview: true,
  confirmLeave: true,
  showTimer: true,
  showNames: true,
  hideSelfView: false,
  chatSound: true,
  notifyJoinLeave: true,
  shareBorder: true,
  shareSurface: 'monitor',
  shareAudio: false,
  shareOptimizeVideo: false,
  shareFloating: true,
};

const KEY = 'nexo.settings';

export const settingsStore = createStore<UserSettings>(readJson(KEY, DEFAULT_SETTINGS));
settingsStore.subscribe(() => writeJson(KEY, settingsStore.get()));

export function updateSettings(patch: Partial<UserSettings>): void {
  settingsStore.patch(patch);
}

export function useSettings(): UserSettings {
  return useStore(settingsStore);
}

// Theme: applied to <html data-theme> and kept in sync with the OS when set to "system".
const media = window.matchMedia('(prefers-color-scheme: dark)');
function applyTheme(): void {
  const { theme } = settingsStore.get();
  const resolved = theme === 'system' ? (media.matches ? 'dark' : 'light') : theme;
  document.documentElement.dataset.theme = resolved;
}
applyTheme();
settingsStore.subscribe(applyTheme);
media.addEventListener('change', applyTheme);
